/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useQuizStore } from '@/store/QuizStore/QuizStore';
import * as XLSX from 'xlsx';
import { useToast } from '@/hooks/useToast';
import { useCreateBulkQuizz } from '@/api/hooks/useQuizz';
import {
  MutationUpdateExerciseArgs,
  QuestionInput,
  QuestionType,
} from '@/api/gql/graphql';
import { useEvaluationStore } from '@/store/EvaluationStore/EvaluationStore';
import { QuizzFormValues } from '@/hooks/useQuizzForm';
import { useDisciplineStore } from '@/store/DisciplineStore/DisciplineStore';
import { useUpdateExercise } from '@/api/hooks/useExercise';
import { useParams } from 'react-router-dom';

export const useQuestionLogic = () => {
  const { isOpenQuizModal, openQuizModal } = useQuizStore();
  const { exerciceTabs, activeTabExercise } = useEvaluationStore();

  const { showToast } = useToast();
  const [file, setFile] = useState('Déposez un fichier xlsx ici');
  const [document, setDocument] = useState<File | null>(null);
  const [quizData, setQuizData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedQuizz, setSelectedQuizz] = useState<QuestionInput>();
  const [selectedType, setSelectedType] = useState<QuestionType | null>(null);
  const [response1Check, setResponse1Check] = useState(false);
  const [response2Check, setResponse2Check] = useState(false);
  const [response3Check, setResponse3Check] = useState(false);
  const [response4Check, setResponse4Check] = useState(false);

  const { lessonId, moduleId } = useParams();
  const { selectedDiscipline } = useDisciplineStore();

  const payload = {
    ...(moduleId ? { section: moduleId } : { lesson: lessonId }),
  };

  const { mutate: createBulkQuizz, isPending } = useCreateBulkQuizz();
  const { mutate: updateExercise, isPending: loadingAddQuestionForExercise } =
    useUpdateExercise();

  const handleNext = () => {
    if (activeTab === 0) {
      if (!document) {
        return showToast(
          'Veuillez ajouter un fichier pour continuer.',
          'error',
          {
            autoClose: 3000,
            position: 'top-right',
          },
        );
      }
    }

    if (activeTab < 2) {
      setActiveTab((prevTab) => prevTab + 1);
    }
  };

  const handlePrevious = () => {
    if (activeTab > 0) {
      setActiveTab((prevTab) => prevTab - 1);
    }
  };

  const handleSelectedQuizz = (quizz: QuestionInput) => {
    setSelectedQuizz(quizz);
  };

  const handleFileChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const uploadedFile = event.target.files?.[0];
      if (uploadedFile) {
        if (
          uploadedFile.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          uploadedFile.name.endsWith('.xlsx')
        ) {
          setter(uploadedFile.name);
          setDocument(uploadedFile);

          const reader = new FileReader();
          reader.onload = (e) => {
            const data = e.target?.result;
            if (data) {
              try {
                const workbook = XLSX.read(data, { type: 'binary' });
                const firstSheetName = workbook.SheetNames[0];
                const firstSheet = workbook.Sheets[firstSheetName];

                const rows = XLSX.utils.sheet_to_json(firstSheet, {
                  header: 1,
                }) as string[][];

                if (rows.length < 2) {
                  showToast('Le fichier est vide ou incomplet.', 'error', {
                    autoClose: 3000,
                    position: 'top-right',
                  });
                  return;
                }

                const headers = rows[0];
                const dataRows = rows.slice(1);

                const requiredHeaders = [
                  'QUESTION',
                  'OPTION A',
                  'OPTION B',
                  'OPTION C',
                  'RIGHT OPTION',
                  'TYPE QUIZZ',
                  'EXPLANATION',
                ];

                const isOptionDPresent = headers.includes('OPTION D');
                const missingHeaders = requiredHeaders.filter(
                  (header) => !headers.includes(header),
                );

                if (missingHeaders.length > 0) {
                  showToast(
                    `Colonnes manquantes : ${missingHeaders.join(', ')}`,
                    'error',
                    {
                      autoClose: 3000,
                      position: 'top-right',
                    },
                  );
                  return;
                }

                const isValidQuestionType = (
                  value: string,
                ): value is QuestionType => {
                  return Object.values(QuestionType).includes(
                    value as QuestionType,
                  );
                };

                const formattedData = dataRows
                  .filter((row) =>
                    row.some((cell) => cell && cell.toString().trim() !== ''),
                  )
                  .map((row, index) => {
                    const questionIndex = headers.indexOf('QUESTION');
                    const typeIndex = headers.indexOf('TYPE QUIZZ');
                    const rightOptionIndex = headers.indexOf('RIGHT OPTION');
                    const explanationIndex = headers.indexOf('EXPLANATION');
                    const responseColumns = isOptionDPresent
                      ? ['OPTION A', 'OPTION B', 'OPTION C', 'OPTION D']
                      : ['OPTION A', 'OPTION B', 'OPTION C'];

                    const keys = ['A', 'B', 'C', 'D'];
                    const correctAnswers = row[rightOptionIndex]
                      ?.toString()
                      .split('&')
                      .map((answer) => answer.trim());
                    const responses = responseColumns
                      .map((colName, index) => {
                        const columnIndex = headers.indexOf(colName);
                        const responseText = row[columnIndex];

                        if (
                          responseText !== undefined &&
                          responseText.toString().trim() !== ''
                        ) {
                          const isRight = correctAnswers?.includes(
                            responseText.toString(),
                          );

                          return {
                            isRight,
                            key: keys[index],
                            response: responseText.toString(),
                          };
                        }

                        return null;
                      })
                      .filter((response) => response !== null);

                    const typeValue = row[typeIndex];
                    const questionType = isValidQuestionType(typeValue)
                      ? typeValue
                      : 'UNKNOWN';

                    return {
                      ...payload,
                      discipline: selectedDiscipline?._id,
                      name:
                        row[questionIndex]?.toString() ||
                        `QUESTION ${index + 1}`,
                      description:
                        row[explanationIndex]?.toString() ||
                        `EXPLANATION ${index + 1}`,
                      type: questionType as QuestionType,
                      responses,
                    };
                  });

                setQuizData(formattedData);
                setFile('Déposez un fichier xlsx ici');
                setActiveTab(1);
              } catch (error) {
                console.error(error);
                showToast('Erreur lors du traitement du fichier.', 'error', {
                  autoClose: 3000,
                  position: 'top-right',
                });
              }
            }
          };
          reader.readAsBinaryString(uploadedFile);
        } else {
          showToast('Veuillez télécharger un fichier xlsx valide.', 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
          setter('Déposez un fichier xlsx ici');
        }
      } else {
        setter('Déposez un fichier xlsx ici');
      }
    };

  const handleSend = () => {
    createBulkQuizz(
      {
        lesson: lessonId,
        questions: quizData,
      },
      {
        onSuccess: () => {
          showToast('Bulk Quizz ajoutée', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          openQuizModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const addMultipleQuestion = () => {
    const questionOutput: MutationUpdateExerciseArgs = {
      id: exerciceTabs[activeTabExercise]?.id,
      input: {
        questions: quizData,
      },
    };

    updateExercise(questionOutput, {
      onSuccess: () => {
        showToast('Question ajoutée avec succès', 'success', {
          autoClose: 3000,
          position: 'top-right',
        });
        openQuizModal();
      },
      onError: (error: { message: string }) => {
        showToast(error?.message, 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
      },
    });
  };

  const formatResponses = (values: QuizzFormValues) => {
    const responses: any = [];
    const responseChecks = [
      response1Check,
      response2Check,
      response3Check,
      response4Check,
    ];

    const responseTexts = [
      values.reponse1?.toString(),
      values.reponse2?.toString(),
      values.reponse3?.toString() ?? null,
      values.reponse4?.toString() ?? null,
    ];

    const keys = ['A', 'B', 'C', 'D'];
    responseChecks.forEach((check, index) => {
      if (responseTexts[index] !== null && responseTexts[index] !== '') {
        responses.push({
          isRight: !!check,
          key: keys[index],
          response: responseTexts[index],
        });
      }
    });

    return responses;
  };

  const resetResponses = () => {
    setResponse1Check(false);
    setResponse2Check(false);
    setResponse3Check(false);
    setResponse4Check(false);
  };

  return {
    activeTab,
    setActiveTab,
    file,
    setFile,
    handleNext,
    handlePrevious,
    handleSend,
    handleFileChange,
    isOpenQuizModal,
    openQuizModal,
    document,
    quizData,
    handleSelectedQuizz,
    selectedQuizz,
    setQuizData,
    setDocument,
    isPending,
    selectedType,
    setSelectedType,
    response1Check,
    setResponse1Check,
    response2Check,
    setResponse2Check,
    response3Check,
    setResponse3Check,
    response4Check,
    setResponse4Check,
    formatResponses,
    resetResponses,
    addMultipleQuestion,
    loadingAddQuestionForExercise,
  };
};
